import { addMinutes, addSeconds, intervalToDuration, isBefore } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import React from 'react'

import { IDate, IDutchAuction, IDateDuration } from '@types'

import { Container, IContainer } from './styles'

interface ICheckerDate extends IContainer {
    startDate: IDate
    dutchAuction?: IDutchAuction
    handleCurrentMintPriceChange(): void
}

const CheckerDate = ({ id, startDate, dutchAuction, handleCurrentMintPriceChange, reverseColor }: ICheckerDate) => {
    const dateZone = () => utcToZonedTime(new Date().toISOString(), 'America/Mexico_City')
    const oneSecond = 1000
    const startDateAsDefaultFormat = new Date(
        startDate.year,
        startDate.month - 1,
        startDate.day,
        startDate.hour,
        startDate.minute,
        startDate.second,
    )

    const [timerMessage, setTimerMessage] = React.useState('')

    const formatTimerMessage = (timer: IDateDuration, timerType: string) => {
        if (timer.years && timer?.years >= 1) {
            return `${timerType} ${timer.years} ${formatMessage(timer.years, 'year')}`
        }
        if (timer.months && timer?.months >= 1) {
            return `${timerType} ${timer.months} ${formatMessage(timer.months, 'month')}`
        }
        if (timer.days && timer?.days >= 1) {
            return `${timerType} ${timer.days} ${formatMessage(timer.days, 'day')}`
        }
        if (timer.hours && timer?.hours >= 1) {
            return `${timerType} ${timer.hours}:${timer.minutes}:${formatSeconds(timer.seconds)}`
        }
        return `${timerType} ${timer.minutes}:${formatSeconds(timer.seconds)}`
    }

    const formatSeconds = (seconds: number | undefined) => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return seconds && seconds > 9 ? seconds : `0${seconds}`
    }
    const formatMessage = (value: number, message: string) => {
        return value > 1 ? `${message}s` : message
    }

    const getTimerMessage = () => {
        const didNotPassedStartDate = isBefore(dateZone(), startDateAsDefaultFormat)
        if (didNotPassedStartDate) {
            const intervalType = 'Start in'
            const durationToStartDate = intervalToDuration({
                start: startDateAsDefaultFormat,
                end: addSeconds(dateZone(), 1),
            })
            return formatTimerMessage(durationToStartDate, intervalType)
        }

        if (!dutchAuction) return ''

        const lastDecresseDate = addMinutes(
            new Date(startDateAsDefaultFormat),
            dutchAuction.numDecreases * dutchAuction.decreaseInterval,
        )
        if (isBefore(lastDecresseDate, dateZone())) return ''

        const oneMinuteInSeconds = 60
        const decreasseInteravalInSeconds = dutchAuction.decreaseInterval * oneMinuteInSeconds
        const lastDecreaseDurationInMS = lastDecresseDate.getTime() - dateZone().getTime()
        const remainingDecresses = Math.floor(lastDecreaseDurationInMS / oneSecond / decreasseInteravalInSeconds)

        const minutesToNextDecress = (dutchAuction.numDecreases - remainingDecresses) * dutchAuction.decreaseInterval
        const intervalToNextPriceDecresse = {
            start: addSeconds(dateZone(), 1),
            end: addMinutes(new Date(startDateAsDefaultFormat), minutesToNextDecress),
        }
        const intervalType = 'Dropping price in'
        const durationToNextPriceDecresse = intervalToDuration(intervalToNextPriceDecresse)
        if (!isBefore(intervalToNextPriceDecresse.end, dateZone())) handleCurrentMintPriceChange()
        return formatTimerMessage(durationToNextPriceDecresse, intervalType)
    }

    React.useEffect(() => {
        window.setInterval(() => {
            setTimerMessage(getTimerMessage())
            dateZone()
        }, oneSecond)
    }, [])

    if (timerMessage === '' && !dutchAuction) return null

    return (
        <Container id={id} reverseColor={reverseColor}>
            {timerMessage}
        </Container>
    )
}

export default CheckerDate
